import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, finalize, filter, firstValueFrom, takeUntil } from 'rxjs';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { CheckboxModule } from 'primeng/checkbox';
import { NsKpisComponent } from './kpis/ns-kpis.component';
import { RevioKpisComponent } from './kpis/revio-kpis.component';
import { BillingService } from '../../../services/billing.service';
import { NotificationsService } from '../../../services/notifications.service';
import { RevioCustomerResolverService } from '../../../services/revio-customer-resolver.service';
import {
  BillingOverviewResponse,
  DomainBillingConfig,
  DomainOverviewRow,
} from '../../../models/billing.model';
import {
  BillingDashboardTourContext,
  BillingDashboardTourService,
} from './billing-dashboard-tour.service';
import { BillingTourCoordinatorService } from './billing-tour-coordinator.service';

type ActiveView = 'overview' | 'manage';

interface ColConfig {
  field: string;
  header: string;
  visible: boolean;
}

const OVERVIEW_DEFAULT_COLS: ColConfig[] = [
  { field: 'customerName', header: 'Rev.io Customer', visible: true },
  { field: 'description', header: 'Description', visible: false },
  { field: 'totalRows', header: 'Products', visible: true },
  { field: 'matchCount', header: 'Match', visible: true },
  { field: 'mismatchCount', header: 'Mismatch', visible: true },
  { field: 'missingInRevioCount', header: 'Miss. Rev.io', visible: true },
  { field: 'extraInRevioCount', header: 'Extra Rev.io', visible: true },
  { field: 'duplicatedCount', header: 'Dup', visible: true },
  { field: 'staticServiceCount', header: 'Static', visible: true },
  { field: 'nonBillableCount', header: 'Non-Billable', visible: true },
  { field: 'trunkingCount', header: 'Trunking', visible: true },
];

const MANAGE_DEFAULT_COLS: ColConfig[] = [
  { field: 'customerName', header: 'Customer', visible: true },
  { field: 'description', header: 'Description', visible: true },
  { field: 'userCount', header: 'Users', visible: true },
  { field: 'isMapped', header: 'Mapping', visible: true },
];

@Component({
  selector: 'app-billing-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    InputTextModule,
    InputSwitchModule,
    TooltipModule,
    ProgressSpinnerModule,
    OverlayPanelModule,
    CheckboxModule,
    NsKpisComponent,
    RevioKpisComponent,
  ],
  templateUrl: './billing-dashboard.component.html',
  styleUrls: ['./billing-dashboard.component.scss'],
})
export class BillingDashboardComponent implements OnInit, OnDestroy {
  private readonly billingService = inject(BillingService);
  private readonly notifications = inject(NotificationsService);
  private readonly router = inject(Router);
  private readonly revioCustomerResolver = inject(RevioCustomerResolverService);
  private readonly tourService = inject(BillingDashboardTourService);
  private readonly tourCoordinator = inject(BillingTourCoordinatorService);
  private readonly destroy$ = new Subject<void>();

  private readonly OVERVIEW_COLS_KEY = 'billing-dashboard-overview-column-config';
  private readonly OVERVIEW_ROWS_KEY = 'billing-dashboard-overview-rows-per-page';
  private readonly MANAGE_COLS_KEY = 'billing-dashboard-manage-column-config';
  private readonly MANAGE_ROWS_KEY = 'billing-dashboard-manage-rows-per-page';
  private readonly OVERVIEW_CACHE_KEY = 'billing-dashboard-overview-cache';
  private readonly OVERVIEW_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  protected activeView: ActiveView = 'overview';

  // Overview state
  protected overviewLoading = false;
  protected overviewData: BillingOverviewResponse | null = null;
  protected overviewColumns: ColConfig[] = [];
  protected overviewRows = 100;
  protected overviewOnlyActionable = false;

  // Manage state
  protected manageLoading = false;
  protected allDomains: DomainBillingConfig[] = [];
  protected filteredDomains: DomainBillingConfig[] = [];
  protected selectedDomains: DomainBillingConfig[] = [];
  protected updatingDomains = new Set<string>();
  protected manageSearchTerm = '';
  protected manageColumns: ColConfig[] = [];
  protected manageRows = 100;

  ngOnInit(): void {
    this.initOverviewColumns();
    this.initManageColumns();
    this.peekOverviewCache();

    this.tourCoordinator.tourRequest$.pipe(
      filter(id => id === 'dashboard'),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.injectTourMockData();
      setTimeout(() => this.startTour(), 150);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private injectTourMockData(): void {
    if (this.overviewData) return;
    this.overviewData = {
      cachedAt: new Date().toISOString(),
      domains: [
        {
          domain: 'example-customer.voice',
          customerId: 100,
          customerName: 'Example Customer',
          description: 'Sample district with billing issues',
          userCount: 45,
          matchCount: 12,
          mismatchCount: 2,
          missingInRevioCount: 1,
          extraInRevioCount: 0,
          duplicatedCount: 1,
          staticServiceCount: 3,
          nonBillableCount: 0,
          trunkingCount: 0,
          totalRows: 18,
        },
        {
          domain: 'healthy-corp.voice',
          customerId: 101,
          customerName: 'All Good Corp',
          description: 'Fully reconciled domain',
          userCount: 20,
          matchCount: 8,
          mismatchCount: 0,
          missingInRevioCount: 0,
          extraInRevioCount: 0,
          duplicatedCount: 0,
          staticServiceCount: 1,
          nonBillableCount: 0,
          trunkingCount: 0,
          totalRows: 9,
        },
        {
          domain: 'new-domain.voice',
          customerId: null,
          customerName: null,
          description: 'Domain pending Rev.io mapping',
          userCount: 10,
          matchCount: 0,
          mismatchCount: 0,
          missingInRevioCount: 0,
          extraInRevioCount: 0,
          duplicatedCount: 0,
          staticServiceCount: 0,
          nonBillableCount: 0,
          trunkingCount: 0,
          totalRows: 0,
        },
      ],
    };
  }

  protected isOverviewColVisible(field: string): boolean {
    return this.overviewColumns.find((c) => c.field === field)?.visible ?? true;
  }

  protected isManageColVisible(field: string): boolean {
    return this.manageColumns.find((c) => c.field === field)?.visible ?? true;
  }

  protected onOverviewColChange(): void {
    this.saveToStorage(this.OVERVIEW_COLS_KEY, this.overviewColumns);
  }

  protected onManageColChange(): void {
    this.saveToStorage(this.MANAGE_COLS_KEY, this.manageColumns);
  }

  protected onOverviewPageChange(event: { rows?: number }): void {
    if (event.rows && event.rows !== this.overviewRows) {
      this.overviewRows = event.rows;
      this.saveToStorage(this.OVERVIEW_ROWS_KEY, this.overviewRows);
    }
  }

  protected onManagePageChange(event: { rows?: number }): void {
    if (event.rows && event.rows !== this.manageRows) {
      this.manageRows = event.rows;
      this.saveToStorage(this.MANAGE_ROWS_KEY, this.manageRows);
    }
  }

  private peekOverviewCache(): void {
    try {
      const raw = localStorage.getItem(this.OVERVIEW_CACHE_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw) as BillingOverviewResponse & { savedAt: number };
      if (Date.now() - cached.savedAt < this.OVERVIEW_CACHE_TTL_MS) {
        this.overviewData = { cachedAt: cached.cachedAt, domains: cached.domains };
      }
    } catch {
      // ignore malformed cache
    }
  }

  private saveOverviewCache(data: BillingOverviewResponse): void {
    try {
      localStorage.setItem(this.OVERVIEW_CACHE_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
    } catch {
      // ignore storage errors
    }
  }

  private clearOverviewCache(): void {
    try {
      localStorage.removeItem(this.OVERVIEW_CACHE_KEY);
    } catch {
      // ignore
    }
  }

  protected loadOverview(): void {
    this.overviewLoading = true;
    this.computeOverview().finally(() => (this.overviewLoading = false));
  }

  protected refreshOverview(): void {
    this.clearOverviewCache();
    this.loadOverview();
  }

  /**
   * Mirrors reconcile's processMultipleDomains logic:
   * 1. Get enabled domains
   * 2. Get NS data for all domains at once
   * 3. Resolve each customer via RevioCustomerResolverService (same strategy chain as reconcile,
   *    including localStorage cache — this is the key step that the backend cannot replicate)
   * 4. Send resolved {domain, customerId} pairs to backend to compute diff stats
   */
  private async computeOverview(): Promise<void> {
    try {
      const configs = await firstValueFrom(this.billingService.getBillingDomainConfigs());
      const enabledDomains = configs.filter((c) => c.enableForBilling).map((c) => c.domain);

      if (!enabledDomains.length) {
        this.overviewData = { cachedAt: new Date().toISOString(), domains: [] };
        return;
      }

      const nsData = await firstValueFrom(this.billingService.getHostedSummaries(enabledDomains));
      const nsRows = (nsData?.rows ?? []) as unknown as Record<string, unknown>[];

      const resolutions = await Promise.allSettled(
        enabledDomains.map(async (domain) => {
          const domainRows = nsRows.filter(
            (r) => r['DOMAIN'] === domain || r['domain'] === domain
          );
          const searchTerm = this.getNsSearchTerm(domainRows, domain);
          const result = await this.revioCustomerResolver.resolveCustomerByDomain(domain, searchTerm);
          return { domain, customer: result?.customer ?? null };
        })
      );

      type Resolution = { domain: string; customer: { customerId: number; customerName: string } | null };
      const resolvedEntries: { domain: string; customerId: number }[] = [];
      const unresolvedDomains: string[] = [];

      for (const r of resolutions) {
        if (r.status === 'fulfilled') {
          const { domain, customer } = r.value as Resolution;
          if (customer) {
            resolvedEntries.push({ domain, customerId: customer.customerId });
          } else {
            unresolvedDomains.push(domain);
          }
        } else {
          // Promise rejected — treat domain as unresolved
          const idx = resolutions.indexOf(r);
          unresolvedDomains.push(enabledDomains[idx]);
        }
      }

      const [statsRows] = await Promise.all([
        resolvedEntries.length
          ? firstValueFrom(this.billingService.computeBillingOverviewStats(resolvedEntries))
          : Promise.resolve([] as DomainOverviewRow[]),
      ]);

      // Build empty rows for unresolved domains so they still appear in the table
      const emptyRows: DomainOverviewRow[] = unresolvedDomains.map((domain) => {
        const domainRows = nsRows.filter((r) => r['DOMAIN'] === domain || r['domain'] === domain);
        const description = this.getNsDescription(domainRows);
        const config = configs.find((c) => c.domain === domain);
        return {
          domain,
          customerId: null,
          customerName: config?.customerName ?? null,
          description: description ?? config?.description ?? null,
          userCount: config?.userCount ?? null,
          matchCount: 0,
          mismatchCount: 0,
          missingInRevioCount: 0,
          extraInRevioCount: 0,
          duplicatedCount: 0,
          staticServiceCount: 0,
          nonBillableCount: 0,
          trunkingCount: 0,
          totalRows: 0,
        };
      });

      const domains = [...statsRows, ...emptyRows].sort((a, b) => a.domain.localeCompare(b.domain));
      const result: BillingOverviewResponse = { cachedAt: new Date().toISOString(), domains };
      this.overviewData = result;
      this.saveOverviewCache(result);
    } catch (err) {
      this.notifications.error(err);
    }
  }

  // Mirrors reconcile's getNsSearchTermForDomain
  private getNsSearchTerm(rows: Record<string, unknown>[], domain: string): string {
    return this.getNsDescription(rows) ?? domain;
  }

  private getNsDescription(rows: Record<string, unknown>[]): string | null {
    if (!rows.length) return null;
    const row = rows[0];
    const desc = ((row['DESCRIPTION'] ?? row['description'] ?? row['Description'] ?? '') as string).trim();
    return desc || null;
  }

  protected exportOverviewCsv(): void {
    if (!this.overviewData) return;

    const visibleCols = [
      { field: 'domain', header: 'Domain' },
      ...this.overviewColumns.filter((c) => c.visible),
    ];

    const escape = (v: unknown): string => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const header = visibleCols.map((c) => escape(c.header)).join(',');
    const rows = this.overviewData.domains.map((row) =>
      visibleCols
        .map((c) => escape((row as unknown as Record<string, unknown>)[c.field]))
        .join(',')
    );

    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-overview-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  protected goToReconcile(domain: string): void {
    this.router.navigate(['/billing/reconcile'], { queryParams: { domain } });
  }

  protected goToMappings(): void {
    this.router.navigate(['/billing/settings/ns-revio-mappings']);
  }

  protected goToMappingsForDomain(domain: string): void {
    this.router.navigate(['/billing/settings/ns-revio-mappings'], { queryParams: { domain } });
  }

  protected openManage(): void {
    this.activeView = 'manage';
    this.loadDomainConfigs();
  }

  protected closeManage(): void {
    this.activeView = 'overview';
  }

  protected startTour(): void {
    const context: BillingDashboardTourContext = {
      openManage: () => {
        this.openManage();
        return Promise.resolve();
      },
      closeManage: () => this.closeManage(),
      loadOverview: () => this.loadOverview(),
    };
    this.tourService.start(context);
  }

  protected filterDomains(): void {
    const term = this.manageSearchTerm.toLowerCase();
    this.filteredDomains = term
      ? this.allDomains.filter(
          (d) =>
            d.domain.toLowerCase().includes(term) ||
            (d.customerName ?? '').toLowerCase().includes(term) ||
            (d.description ?? '').toLowerCase().includes(term)
        )
      : [...this.allDomains];
  }

  protected toggleDomain(config: DomainBillingConfig): void {
    this.updatingDomains.add(config.domain);
    this.billingService
      .updateDomainEnableForBilling(config.domain, config.enableForBilling)
      .pipe(finalize(() => this.updatingDomains.delete(config.domain)))
      .subscribe({
        error: (err) => {
          config.enableForBilling = !config.enableForBilling;
          this.notifications.error(err);
        },
      });
  }

  protected bulkEnable(): void {
    this.bulkUpdate(true);
  }

  protected bulkDisable(): void {
    this.bulkUpdate(false);
  }

  protected hasIssues(row: DomainOverviewRow): boolean {
    return row.mismatchCount > 0 || row.missingInRevioCount > 0 || row.extraInRevioCount > 0 || row.duplicatedCount > 0;
  }

  protected get filteredOverviewDomains(): DomainOverviewRow[] {
    const domains = this.overviewData?.domains ?? [];
    return this.overviewOnlyActionable ? domains.filter((row) => this.hasIssues(row)) : domains;
  }

  private loadDomainConfigs(): void {
    this.manageLoading = true;
    this.billingService.getBillingDomainConfigs()
      .pipe(finalize(() => (this.manageLoading = false)))
      .subscribe({
        next: (domains) => {
          this.allDomains = domains;
          this.filteredDomains = [...domains];
          this.manageSearchTerm = '';
        },
        error: (err) => this.notifications.error(err),
      });
  }

  private bulkUpdate(enableForBilling: boolean): void {
    if (!this.selectedDomains.length) return;

    const updates = this.selectedDomains.map((d) => ({
      domain: d.domain,
      enableForBilling,
    }));

    this.billingService.bulkUpdateEnableForBilling(updates).subscribe({
      next: () => {
        this.selectedDomains.forEach((d) => {
          d.enableForBilling = enableForBilling;
        });
        this.selectedDomains = [];
      },
      error: (err) => this.notifications.error(err),
    });
  }

  private initOverviewColumns(): void {
    this.overviewColumns = this.loadColConfig(this.OVERVIEW_COLS_KEY, OVERVIEW_DEFAULT_COLS);
    this.overviewRows = this.loadRowsConfig(this.OVERVIEW_ROWS_KEY, 100);
  }

  private initManageColumns(): void {
    this.manageColumns = this.loadColConfig(this.MANAGE_COLS_KEY, MANAGE_DEFAULT_COLS);
    this.manageRows = this.loadRowsConfig(this.MANAGE_ROWS_KEY, 100);
  }

  private loadColConfig(key: string, defaults: ColConfig[]): ColConfig[] {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved) as ColConfig[];
        const savedMap = new Map(parsed.map((c) => [c.field, c]));
        return defaults.map((col) => {
          const saved = savedMap.get(col.field);
          return saved && typeof saved.visible === 'boolean'
            ? { ...col, visible: saved.visible }
            : col;
        });
      }
    } catch {
      // ignore
    }
    return [...defaults];
  }

  private loadRowsConfig(key: string, fallback: number): number {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }
    } catch {
      // ignore
    }
    return fallback;
  }

  private saveToStorage(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  }
}
